import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import Home from './components/Home';
import Productos from './components/Products';
import ProductDetail from './components/ProductDetail';
import CategoryProducts from './components/CategoryProducts';
import Navbar from './components/Navbar';
import History from './components/History';
import Login from './components/Login';
import Cart from './components/Cart';
import ContactForm from './components/ContactForm';
import Footer from './components/Footer';
import AdminDashboard from './components/AdminDashboard';
import AdminStaffUsersPage from './components/AdminStaffUsersPage';
import Company from './components/Company';
import SubscriberList from './components/SubscriberList';
import MassEditor from './components/MassEditor';
import SalesReport from './components/SalesReport';
import SalesHistory from './components/SalesHistory';
import DiscountConfig from './components/DiscountConfig';
import CostoEnvio from './components/CostoEnvio';
import LocalSales from './components/LocalSales';
import StockManagement from './components/StockManagement';
import InvoiceGenerator from './components/InvoiceGenerator';
import QRCodeComponent from './components/QRCodeComponent';
import Success from './components/PaginasMP/success';
import Failure from './components/PaginasMP/failure';
import Pending from './components/PaginasMP/pending';
import OrderConfirmed from './components/OrderConfirmed';
import WallStreetChart from './components/WallStreetChart';
import ListaMayorista from './components/ListaMayorista';
import NewsletterManager from './components/NewsletterManager';
import AdminExpenses from './components/AdminExpenses';
import AdminCustomersPage from './components/AdminCustomersPage';
import AdminSuppliersPage from './components/AdminSuppliersPage';
import Factory from './components/Factory';
import AnimatedBackground from './components/AnimatedBackground';
import Notification from './components/Notification';
import ChatWidget from './components/ChatWidget';
import Subscription from './components/Subscription';
import { EditModeProvider, useEditMode } from './contexts/EditModeContext';
import {
  isFullAdminFromStorage,
  hasStaffFeature,
  canAccessAdminShell,
  isStaffOrViewerFromStorage
} from './utils/staffAccess';
import { initStaffSessionWatch } from './utils/staffSessionGuard';
import { initializeStoreTheme } from './utils/storeTheme';

import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

import './App.css';

const AppContent = ({ cart, setCart, removeFromCart, clearCart, updateCartItemQuantity, websiteURL }) => {
  const [notification, setNotification] = useState({ message: '', show: false, type: 'success' });
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const { isEditMode } = useEditMode();

  const fullAdmin = isFullAdminFromStorage();
  const shell = canAccessAdminShell();
  const staffOrViewerSession = isStaffOrViewerFromStorage();

  const showNotification = (message, type = 'success') => {
    setNotification({ message, show: true, type });
  };

  const closeNotification = () => {
    setNotification((prev) => ({ ...prev, show: false }));
  };

  useEffect(() => {
    if (isEditMode) return;
    if (staffOrViewerSession) return;

    const hasShown = localStorage.getItem('subscriptionModalShown') === 'true';
    if (hasShown) return;

    const timer = setTimeout(() => {
      setShowSubscriptionModal(true);
      localStorage.setItem('subscriptionModalShown', 'true');
    }, 50000);
    return () => clearTimeout(timer);
  }, [isEditMode, staffOrViewerSession]);

  useEffect(() => {
    if (staffOrViewerSession) setShowSubscriptionModal(false);
  }, [staffOrViewerSession]);

  return (
    <div className="App">
      <AnimatedBackground />
      <Navbar cart={cart} />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/historia" element={<History />} />
        <Route path="/empresa" element={<Company />} />
        <Route path="/contacto" element={<ContactForm />} />
        <Route path="/productos" element={<Productos cart={cart} setCart={setCart} />} />
        <Route path="/producto/:productId" element={<ProductDetail cart={cart} setCart={setCart} />} />
        <Route path="/categoria/:categoryName" element={<CategoryProducts cart={cart} setCart={setCart} />} />
        <Route path="/lista-mayorista" element={<ListaMayorista cart={cart} setCart={setCart} />} />
        <Route path="/comprar" element={<Productos cart={cart} setCart={setCart} />} />
        <Route path="/carrito" element={<Cart cart={cart} setCart={setCart} removeFromCart={removeFromCart} clearCart={clearCart} updateCartItemQuantity={updateCartItemQuantity} />} />
        <Route path="/login" element={<Login />} />
        {(fullAdmin || shell) && <Route path="/admin" element={<AdminDashboard />} />}
        {(fullAdmin ||
          hasStaffFeature('customers') ||
          hasStaffFeature('localSales') ||
          hasStaffFeature('adminOrders') ||
          hasStaffFeature('adminSalesMp')) && (
          <Route path="/admin/clientes" element={<AdminCustomersPage />} />
        )}
        {(fullAdmin ||
          hasStaffFeature('suppliers') ||
          hasStaffFeature('localSales') ||
          hasStaffFeature('adminOrders') ||
          hasStaffFeature('adminSalesMp')) && (
          <Route path="/admin/proveedores" element={<AdminSuppliersPage />} />
        )}
        {fullAdmin && <Route path="/admin/usuarios" element={<AdminStaffUsersPage />} />}
        {(fullAdmin || hasStaffFeature('subscribers')) && <Route path="/subscriptores" element={<SubscriberList />} />}
        {(fullAdmin || hasStaffFeature('massEditor')) && <Route path="/editor-masivo" element={<MassEditor />} />}
        {(fullAdmin || hasStaffFeature('salesHistory')) && <Route path="/historial-ventas" element={<SalesHistory />} />}
        {(fullAdmin || hasStaffFeature('analytics')) && <Route path="/analiticas" element={<SalesReport />} />}
        {(fullAdmin || hasStaffFeature('localSales')) && <Route path="/local" element={<LocalSales />} />}
        {(fullAdmin || hasStaffFeature('discounts')) && <Route path="/configuracion-descuentos" element={<DiscountConfig />} />}
        {(fullAdmin || hasStaffFeature('shippingCosts')) && <Route path="/costo-envios" element={<CostoEnvio />} />}
        {(fullAdmin || hasStaffFeature('stock')) && <Route path="/gestion-stock" element={<StockManagement />} />}
        {fullAdmin && <Route path="/facturador" element={<InvoiceGenerator />} />}
        {(fullAdmin || hasStaffFeature('balance')) && <Route path="/balance" element={<WallStreetChart />} />}
        {(fullAdmin || hasStaffFeature('newsletter')) && <Route path="/newsletter" element={<NewsletterManager />} />}
        {(fullAdmin || hasStaffFeature('expenses')) && <Route path="/admin/gastos" element={<AdminExpenses showNotification={showNotification} />} />}
        {(fullAdmin || hasStaffFeature('factory')) && <Route path="/factory" element={<Factory />} />}
        <Route path="/qr" element={<QRCodeComponent url={websiteURL} />} />
        <Route path="/success" element={<Success clearCart={clearCart} />} />
        <Route path="/failure" element={<Failure />} />
        <Route path="/pending" element={<Pending />} />
        <Route path="/pedido-enviado" element={<OrderConfirmed />} />
      </Routes>
      <Footer />
      {!staffOrViewerSession && <ChatWidget cart={cart} setCart={setCart} />}
      <Notification
        message={notification.message}
        show={notification.show}
        type={notification.type}
        onClose={closeNotification}
      />
      {showSubscriptionModal && !isEditMode && !staffOrViewerSession && (
        <div
          className="subscription-modal-overlay"
          onClick={() => setShowSubscriptionModal(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Suscríbete para promociones"
        >
          <div
            className="subscription-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="subscription-modal-close"
              onClick={() => setShowSubscriptionModal(false)}
              aria-label="Cerrar"
            >
              ✕
            </button>
            <Subscription />
          </div>
        </div>
      )}
    </div>
  );
};

const App = () => {
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem('cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });

  useEffect(() => {
    return initStaffSessionWatch();
  }, []);

  useEffect(() => {
    initializeStoreTheme();
  }, []);

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  const removeFromCart = (id) => {
    setCart(cart.filter((item) => item.id !== id));
  };

  const clearCart = () => {
    setCart([]);
  };

  const updateCartItemQuantity = (itemId, newQuantity) => {
    setCart(cart.map((item) => (item.id === itemId ? { ...item, quantity: newQuantity } : item)));
  };

  const recaptchaKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || process.env.REACT_APP_RECAPTCHA_FRONTEND;
  const websiteURL = process.env.REACT_APP_WEBSITE_URL || 'https://www.priball.com';
  return (
    <GoogleReCaptchaProvider reCaptchaKey={recaptchaKey || ''}>
      <EditModeProvider>
        <Router>
          <AppContent
            cart={cart}
            setCart={setCart}
            removeFromCart={removeFromCart}
            clearCart={clearCart}
            updateCartItemQuantity={updateCartItemQuantity}
            websiteURL={websiteURL}
          />
        </Router>
      </EditModeProvider>
    </GoogleReCaptchaProvider>
  );
};

export default App;
